import { useCallback, useEffect, useRef } from "react";
import _ from "underscore";
import axios from "axios";

const throttleDelay = 400;

/**
 * Hook that throttles apis so that too many React rerenders doesn't affect the API as much.
 * This also takes care of cancellation tokens.
 * usage:
 *** useDebouncedApiEffect(fetchCall, [deps]);
 * @param {Function} apiFunction - receives { cancelToken }; will be wrapped with useCallback with the same deps.
 * @param {Array} deps - dependencies array that triggers the API
 * @param {number} wait - debounce delay in ms
 */
export function useDebouncedApiEffect(apiFunction, deps = [], wait = throttleDelay) {
    const axiosSourceRef = useRef();

    const callbackApiFunction = useCallback(apiFunction, [deps]);

    useEffect(() => {
        const callApi = (...args) => {
            if (axiosSourceRef.current) {
                axiosSourceRef.current.cancel("Canceled previous request");
            }

            const source = axios.CancelToken.source();
            axiosSourceRef.current = source;

            return callbackApiFunction({ cancelToken: source.token }, ...args);
        };

        // Create a new debounced function every time deps change
        const debounced = _.debounce(callApi, wait);
        debounced();

        return () => {
            debounced.cancel && debounced.cancel();
            axiosSourceRef.current && axiosSourceRef.current.cancel("Component unmounted");
        };
    }, deps);
}
